<template>
<div v-if="!initializing" class="confirm-infos">
  <template v-if="isTrial">
    <p>
      Your {{ trialDays }} days trial period is about to begin. You will have access to PM2 Plus
      features and connect up to {{ plan.process_limit }} processes. You will be charged ${{
      taxedPrice
      }}
      / {{ plan.interval }} (tax included) once the trial is finished (on the {{ trialEndDate }}),
      but you can cancel any time during your trial.
    </p>
  </template>

  <template v-else>
    <p v-if="plan.process_limit" class="mt-2">
      Monitoring plan is for:<br/>
      <b class="price-show">{{ plan.process_limit }} processes</b>
    </p>
    <p v-else>
      You choose to cancel your subscription.
    </p>

    <h2 v-if="isPremiumDowngrade">
      <template v-if="currentTrialEndDate">
        Your trial will be immediately canceled.
      </template>
      <template v-else-if="periodEndDate">
        Your bucket will be downgraded on {{ periodEndDate }}.
      </template>
    </h2>

      <h2 v-if="coupon.valid" class="mt-2">
        Enjoy a reduced price of ${{ taxedPrice }} (tax included) per {{ plan.interval }}
        {{ duration }}
        <span v-if="coupon.duration !== 'forever'">
          and ${{ afterPrice }} per {{ plan.interval }} after that </span
        >.
      </h2>
      <p v-else-if="taxedPrice > 0" class="mt-2">
        Price is:<br/>
        <b class="price-show">${{ afterPrice }}</b>  <span v-if="tax > 0">({{tax}}% tax included)</span>
        <br/>
        per <b class="price-show">{{ plan.interval }}</b><br/>

        <div class="mt-1">
          Card charged will be:
          <div class="credit-card-fake mt-1">
            {{card.name}}<br/>
            **** **** **** {{card.last4}}
          </div>
        </div>
        <a class="edit-card" @click="changeCard()">Edit card</a>

      </p>
    </template>

    <VProgressCircular
      v-if="loading"
      indeterminate
      color="primary"
      :size="24"
      :width="4"
      class="mt-4"
      />
      <VBtn v-else class="button-next mt-4 mb-2" style="margin:0 auto" color="primary" @click="$emit('submit')">
        <b>{{ taxedPrice && !isTrial ? 'Confirm' : 'Continue' }}</b>
      </VBtn>
  </div>
  <div v-else class="text-center">
    <VProgressCircular indeterminate color="primary" :size="32" :width="8" class="my-4" />
  </div>
</template>

<script>
import moment from 'moment'
import { formatDate } from '@/utils/format'
import * as act from '@/store/actions'
import { BILLING_CARDS_PAGE } from '@/router'

export default {
  name: 'BillingCoupon',
  props: {
    plan: Object,
    isTrial: Boolean,
    coupon: Object,
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      initializing: true,
      tax: null,
      periodEndDate: null,
      currentTrialEndDate: null,
    }
  },

  computed: {
    price() {
      return this.plan.amount / 100
    },

    isPremiumDowngrade() {
      return this.$store.getters.currentPlan.is_premium && !this.plan.is_premium
    },

    reducedPrice() {
      if (!this.coupon.percent_off) return this.price
      return this.price - (this.price * this.coupon.percent_off) / 100
    },

    taxedPrice() {
      return (this.reducedPrice * this.tax) / 100 + this.reducedPrice
    },

    afterPrice() {
      return (this.price * this.tax) / 100 + this.price
    },

    card() {
      return this.$store.getters.billing({}).cards.find(card => card.default)
    },
    duration() {
      if (this.plan.interval !== 'year' && this.coupon.duration === 'repeating') {
        return `for ${this.coupon.duration_in_months} month${
          this.coupon.duration_in_months > 1 ? 's' : ''
        }`
      } else if (this.coupon.duration === 'once') {
        return 'once'
      } else if (this.coupon.duration === 'forever') {
        return 'forever'
      }
      return ''
    },

    trialDays() {
      return this.$store.state.plans.trial.for
    },

    vat() {
      return this.$store.getters.billingMetadata({}).vat_number
    },
    trialEndDate() {
      return formatDate(
        moment()
          .add(this.$store.state.plans.trial.for, 'days')
          .toDate(),
      )
    },
  },

  created() {
    this.initializing = true
    Promise.all([this.initializeSubscription(), this.initializeTax()]).then(() => {
      this.initializing = false
    })
  },

  methods: {
    changeCard() {
      this.$router.push({ name: BILLING_CARDS_PAGE })
      this.$emit('close')
    },
    initializeSubscription() {
      this.$store.getters.currentBillingSubscription.then(sub => {
        if (!sub) return
        if (sub.current_period_end) {
          this.periodEndDate = formatDate(new Date(sub.current_period_end * 1000))
        }
        if (sub.status === 'trialing') {
          this.currentTrialEndDate = formatDate(new Date(sub.trial_end * 1000))
        }
      })
    },

    initializeTax() {
      const card = this.$store.getters.billing({}).cards.find(card => card.default)
      const vatNumber = this.$store.getters.billingMetadata({}).vat_number

      let promise

      if (
        // Only set tax if the plan is not free
        this.plan.amount &&
        // Only set tax if we have a card
        card &&
        // Only set tax if there is no VAT number or the country is France
        (!vatNumber || card.country === 'FR')
      ) {
        promise = this.$store.dispatch(act.RETRIEVE_VAT, { country: card.country })
      } else {
        promise = Promise.resolve(0)
      }

      return promise.then(tax => {
        this.tax = tax || 0
      })
    },
  },
}
</script>

<style scoped>
.progress-circular,
.button-next {
  display: block;
  margin-left: auto;
}
.confirm-infos {
  font-size: 19px;
  text-align: center;
}

.credit-card-fake {
  background-color: var(--contrast);
  padding: 20px;
  border-radius: 5px;
  width: 300px;
  margin: 0 auto;
}
.price-show {
  font-size: 24px;
}
.edit-card {
  font-size: 13px;
  font-style: italic;
}
</style>
